import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  IconButton,
} from '@material-ui/core';
import { Edit } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';

import PaginationData from 'types/models/PaginationData';
import AppContext from 'contexts/AppContext';
import EditStockModuleContext from 'contexts/EditStockModuleContext';
import { StockSupplier } from 'types/models/StockReceipt';
import { getStockSuppliers } from 'services/supplierServices';
import { getSimpleVenuesWithStockModuleActive } from 'services/venuesServices';
import SelectField from 'components/SelectField';
import BooleanIcon from 'components/common/BooleanIcon';
import { formatPhone } from 'utils/formatPhone';
import CopyEmail from 'components/common/CopyEmail';
import { mapToSelectOptions } from 'utils/select';
import Venue from 'types/models/Venue';
import UserRoleType from 'types/enums/UserRoleType';
import useSetupInitialState from 'hooks/useSetupInitialState';

export default function StockSuppliersPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const { authData, currentPageListNumber, setCurrentPageListNumber } = React.useContext(AppContext);
  const { setSelectedSupplier } = React.useContext(EditStockModuleContext);
  const entity = useSetupInitialState();
  const isVenueEditor = authData?.user?.role === UserRoleType.VenueEditor;

  const [venues, setVenues] = React.useState<Venue[]>([]);
  const [venueId, setVenueId] = React.useState<string>('');
  const [paginationData, setPaginationData] = React.useState<PaginationData<StockSupplier>>();

  React.useEffect(() => {
    let mounted = true;

    async function loadVenues() {
      if (isVenueEditor) {
        return;
      }
      try {
        const response = await getSimpleVenuesWithStockModuleActive();
        if (mounted) setVenues(response ?? []);
      } catch (error) {
        console.error(error);
      }
    }

    loadVenues();

    return () => {
      mounted = false;
    };
  }, [isVenueEditor]);

  React.useEffect(() => {
    if (!isVenueEditor) return;
    if (!entity?.id) return;
    setVenueId(entity.id);
  }, [isVenueEditor, entity]);

  const loadData = React.useCallback(
    async (page?: number, searchTerm?: string) => {
      try {
        const paginationData = await getStockSuppliers(page, searchTerm, venueId);
        if (paginationData) setPaginationData(paginationData);
      } catch (error) {
        console.log(error);
      }
    },
    [venueId],
  );

  React.useEffect(() => {
    if (!venueId) {
      setPaginationData(undefined);
      return;
    }
    if (currentPageListNumber) {
      loadData(currentPageListNumber);
    }
  }, [currentPageListNumber, location.search, loadData, venueId]);

  const renderContent = (paginationData: StockSupplier[]): JSX.Element => {
    return (
      <>
        {paginationData.map((supplier) => (
          <TableRow key={supplier.id}>
            <TableCell>{supplier.businessName}</TableCell>
            <TableCell>{supplier.taxId}</TableCell>
            <TableCell>
              <CopyEmail email={supplier.email} />
            </TableCell>
            <TableCell>{formatPhone(supplier.phone)}</TableCell>
            <TableCell>
              <BooleanIcon value={supplier.isActive} />
            </TableCell>
            <TableCell>{renderEditButton(supplier)}</TableCell>
          </TableRow>
        ))}
      </>
    );
  };

  const renderEditButton = (supplier: StockSupplier): JSX.Element => {
    return (
      <IconButton
        color="inherit"
        onClick={() => {
          if (!supplier) return;
          setSelectedSupplier(supplier);
          history.push(`/private/stock/suppliers/${supplier.id}/edit`);
        }}
      >
        <Edit />
      </IconButton>
    );
  };

  return (
    <>
      {!isVenueEditor && (
        <SelectField
          name="venueId"
          label={t('venue')}
          placeholder={t('select')}
          value={venueId}
          options={mapToSelectOptions(venues, { label: 'name' })}
          setFieldValue={(_, value) => setVenueId(value as string)}
          touched={false}
          error={false}
          isMulti={false}
          isSearchable
          isClearable={false}
          backspaceRemovesValue={false}
          onBlur={() => undefined}
          isDisabled={false}
        />
      )}
      <Button
        className="btn-50 my-6"
        variant="contained"
        color="primary"
        onClick={() => {
          history.push('/private/stock/suppliers/create');
        }}
      >
        {t('stock.suppliers.create')}
      </Button>

      <TableContainer className="table-container" component={Paper}>
        <Table>
          <TableHead className="table-header">
            <TableRow>
              <TableCell>{t('stock.suppliers.table.business-name')}</TableCell>
              <TableCell>{t('stock.suppliers.table.tax-id')}</TableCell>
              <TableCell>{t('stock.suppliers.table.email')}</TableCell>
              <TableCell>{t('stock.suppliers.table.phone')}</TableCell>
              <TableCell>{t('stock.suppliers.table.status')}</TableCell>
              <TableCell>{t('stock.suppliers.table.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginationData?.data && paginationData?.data.length > 0 ? (
              renderContent(paginationData.data)
            ) : (
              <TableRow>
                <TableCell component="th" scope="row">
                  {t('stock.suppliers.table.no-results')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {paginationData && (
        <TablePagination
          component="div"
          count={paginationData.total}
          page={paginationData.currentPage - 1}
          onPageChange={(_, page: number) => {
            setCurrentPageListNumber(page + 1);
          }}
          rowsPerPage={paginationData.perPage}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) => {
            return `${from}-${to} ${t('pagination-of')} ${count !== -1 ? count : '0'}`;
          }}
        />
      )}
    </>
  );
}
